import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Wrench, Activity, FlaskConical, ShieldCheck, FileCode2, Trash2, Loader2, Check, AlertTriangle } from 'lucide-react';
import { encryptChunk, decryptChunk, CRYPTO_WORKER_URL, isWorkerActive } from './cryptoClient';
import { chunkFingerprint, CHUNK_SIZE } from './uploads';
import { getMachineEvents, subscribeMachineLog, clearMachineLog } from './machineLog';

/**
 * Maschinenraum — die Wartungsklappe.
 *
 * Grundsatz, der auch in der Oberfläche steht: Dieses Panel beweist für sich genommen
 * gar nichts. Es ist unser Code, und unser Code kann alles behaupten. Der Wert liegt
 * darin, dass es genau sagt, wo im Browser die Wahrheit steht, und dass jede Zahl hier
 * gemessen ist statt ausgedacht.
 *
 * Regeln für diese Datei:
 *  - Keine erfundenen Werte. Was nicht gemessen wurde, wird nicht angezeigt.
 *  - Kein Zustand von hier geht ins Netz, in einen Fehlerbericht oder auf die Platte.
 *  - Die Demos rechnen auf Kopien. Echte Uploads werden nie manipuliert.
 */

const TABS = [
  { id: 'log', label: 'Mitschnitt', icon: Activity },
  { id: 'probe', label: 'Probelauf', icon: FlaskConical },
  { id: 'tests', label: 'Selbsttests', icon: ShieldCheck },
  { id: 'source', label: 'Quelltext', icon: FileCode2 },
];

// Gleiche Schreibweise wie in der Upload-Karte, damit die Zahlen hier und dort
// erkennbar dieselben sind.
function fmtBytes(n) {
  if (n === null || n === undefined) return '–';
  if (n === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)));
  return `${parseFloat((n / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function fmtMs(ms) {
  if (ms === null || ms === undefined) return '–';
  return ms < 1000 ? `${ms.toFixed(0)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

function hexDump(bytes, limit = 32) {
  const slice = bytes.subarray(0, Math.min(limit, bytes.length));
  return Array.from(slice).map((b) => b.toString(16).padStart(2, '0')).join(' ');
}

function asciiDump(bytes, limit = 32) {
  const slice = bytes.subarray(0, Math.min(limit, bytes.length));
  return Array.from(slice).map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '·')).join('');
}

async function sha256Hex(buffer) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const EVENT_STYLE = {
  encrypt: { label: 'verschlüsselt', tone: 'accent' },
  decrypt: { label: 'entschlüsselt', tone: 'accent' },
  sign: { label: 'Adresse geholt', tone: 'muted' },
  put: { label: 'hochgeladen', tone: 'plain' },
  get: { label: 'geladen', tone: 'plain' },
};

function Row({ label, value, mono = true }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-xs vault-text-subtle shrink-0">{label}</span>
      <span className={`text-xs vault-text text-right break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function Note({ children, tone = 'plain' }) {
  const cls = tone === 'warn' ? 'vault-banner-warn' : 'vault-glass';
  return (
    <div className={`${cls} rounded-lg p-3 text-xs vault-text-subtle leading-relaxed`}>{children}</div>
  );
}

export default function Maschinenraum({ open, onClose, getEncryptionKey }) {
  const [tab, setTab] = useState('log');
  const [events, setEvents] = useState(() => getMachineEvents());

  useEffect(() => subscribeMachineLog(setEvents), []);

  // Escape schließt die Klappe. Wichtig für das Versprechen "stört nicht".
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="vault-settings-overlay fixed inset-0 z-[96] p-4 sm:p-6"
      style={{ background: 'var(--vault-overlay)' }}
      onClick={onClose}
    >
      <div
        className="vault-settings-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Maschinenraum"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg font-semibold vault-text flex items-center gap-2">
              <Wrench className="w-5 h-5 vault-text-accent" />
              Maschinenraum
            </h3>
            <p className="text-xs vault-text-subtle mt-1 max-w-xl leading-relaxed">
              Die Wartungsklappe zum Tresor. Hier steht, was Ihr Browser tatsächlich getan hat.
              Alle Zahlen sind gemessen, keine ist ausgedacht.
            </p>
          </div>
          <button type="button" onClick={onClose} className="vault-btn-ghost px-2 py-1 shrink-0" aria-label="Maschinenraum schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <Note>
          <strong className="vault-text">Wichtig, damit hier niemand das Falsche glaubt.</strong>{' '}
          Dieses Panel beweist für sich genommen nichts. Es ist unser Code, und unser Code könnte
          Ihnen hier alles Mögliche erzählen. Der eigentliche Beweis liegt im Netzwerk-Tab Ihres
          Browsers. Der Maschinenraum sagt Ihnen nur genau, wo Sie hinschauen müssen.
        </Note>

        <div className="flex gap-1 mt-4 mb-4 border-b border-[var(--vault-border)] overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === id
                  ? 'border-[var(--vault-accent)] vault-text font-medium'
                  : 'border-transparent vault-text-subtle hover:opacity-80'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {tab === 'log' && <LogTab events={events} />}
        {tab === 'probe' && <ProbeTab getEncryptionKey={getEncryptionKey} />}
        {tab === 'tests' && <TestsTab getEncryptionKey={getEncryptionKey} />}
        {tab === 'source' && <SourceTab />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Mitschnitt */

function LogTab({ events }) {
  const totals = useMemo(() => {
    let plain = 0; let cipher = 0; let enc = 0; let dec = 0; let put = 0; let get = 0;
    for (const e of events) {
      if (e.kind === 'encrypt') { plain += e.plainBytes || 0; cipher += e.cipherBytes || 0; enc += 1; }
      if (e.kind === 'decrypt') { dec += 1; }
      if (e.kind === 'put') { put += e.cipherBytes || 0; }
      if (e.kind === 'get') { get += e.cipherBytes || 0; }
    }
    return { plain, cipher, enc, dec, put, get };
  }, [events]);

  const hosts = useMemo(() => {
    const set = new Set();
    for (const e of events) if (e.targetHost) set.add(e.targetHost);
    return Array.from(set);
  }, [events]);

  return (
    <div className="space-y-3">
      <Note>
        Was seit dem Laden dieser Seite durch die Verschlüsselung gelaufen ist. Dieser Mitschnitt
        liegt ausschließlich im Arbeitsspeicher. Er wird nirgendwo gespeichert und nirgendwohin
        gesendet. Ein Neuladen der Seite leert ihn, und das Sperren des Tresors ebenfalls.
        Eine Ausnahme: Wenn beim Sperren noch Uploads laufen und Sie eingestellt haben, dass sie
        zu Ende laufen dürfen, bleibt ihr Mitschnitt stehen und wächst weiter, bis sie fertig sind.
      </Note>

      {events.length === 0 ? (
        <div className="vault-empty-state text-center py-8 px-4">
          <Activity className="w-8 h-8 mx-auto mb-3 vault-text-muted" />
          <p className="text-sm vault-text">Noch nichts passiert.</p>
          <p className="text-xs vault-text-subtle mt-2 max-w-md mx-auto leading-relaxed">
            Laden Sie eine Datei hoch oder herunter, dann füllt sich diese Liste live mit.
            Wenn Sie nichts hochladen wollen, nehmen Sie den Probelauf im Nachbartab. Der verschlüsselt
            eine Datei Ihrer Wahl, ohne irgendetwas zu senden.
          </p>
        </div>
      ) : (
        <>
          <div className="vault-glass rounded-lg p-3">
            <Row label="Blöcke verschlüsselt" value={totals.enc} />
            <Row label="Blöcke entschlüsselt" value={totals.dec} />
            {totals.plain > 0 && <Row label="Klartext hinein" value={fmtBytes(totals.plain)} />}
            {totals.cipher > 0 && <Row label="Geheimtext heraus" value={fmtBytes(totals.cipher)} />}
            {totals.put > 0 && <Row label="Ins Netz gesendet (verschlüsselt)" value={fmtBytes(totals.put)} />}
            {totals.get > 0 && <Row label="Aus dem Netz geholt (verschlüsselt)" value={fmtBytes(totals.get)} />}
            {hosts.length > 0 && <Row label="Gegenstellen" value={hosts.join(', ')} />}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs vault-text-subtle">{events.length} Einträge, neueste zuletzt</span>
            <button type="button" onClick={clearMachineLog} className="vault-btn-ghost text-xs px-2 py-1 flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
              Mitschnitt leeren
            </button>
          </div>

          <div className="space-y-1.5 max-h-[38vh] overflow-y-auto pr-1">
            {events.map((e) => <EventRow key={e.id} e={e} />)}
          </div>

          <Note>
            <strong className="vault-text">So prüfen Sie das gegen.</strong> Öffnen Sie den Netzwerk-Tab
            und vergleichen Sie die Byte-Zahlen oben mit dem, was dort wirklich übertragen wurde.
            Sie müssen zusammenpassen. Und im Objektpfad steht nie ein Dateiname, sondern nur
            eine Nummer.
          </Note>
        </>
      )}

      <Coverage />
    </div>
  );
}

/**
 * Was mitgeschrieben wird und was nicht. Eine Lücke, die im Panel steht, ist ehrlicher
 * als eine Liste, die vollständig aussieht und es nicht ist.
 */
function Coverage() {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="vault-btn-ghost text-xs px-2 py-1">
        {open ? 'Abdeckung verbergen' : 'Was wird hier mitgeschrieben, was nicht?'}
      </button>
      {open && (
        <div className="vault-glass rounded-lg p-3 mt-2 text-xs vault-text-subtle leading-relaxed space-y-1.5">
          <div className="vault-text font-medium">Mitgeschrieben</div>
          <div>Upload in die Cloud, mit Verschlüsselung, Adressabruf und Übertragung.</div>
          <div>Upload, der nur auf diesem Gerät bleibt, mit Verschlüsselung.</div>
          <div>Download aus der Cloud, auch der Weg für sehr große Dateien.</div>
          <div className="vault-text font-medium pt-1.5">Nicht mitgeschrieben</div>
          <div>
            Der Download einer Datei, die schon in diesem Browser zwischengespeichert liegt.
            Dieser Weg läuft in der Krypto-Kerndatei, und dort haben wir bewusst keinen
            Rückkanal eingebaut. Eine von außen übergebene Funktion mitten in der
            Entschlüsselungsschleife wäre für jeden, der den Kern prüft, genau die Stelle,
            die er zusätzlich lesen müsste. Diese Lücke steht lieber hier, als dass die
            Liste oben vollständig aussieht.
          </div>
        </div>
      )}
    </div>
  );
}

function EventRow({ e }) {
  const style = EVENT_STYLE[e.kind] || { label: e.kind, tone: 'plain' };
  const overhead = (e.cipherBytes && e.plainBytes) ? e.cipherBytes - e.plainBytes : null;

  return (
    <div className="vault-glass rounded-lg px-3 py-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-medium vault-text">
          <span className={style.tone === 'accent' ? 'vault-text-accent' : 'vault-text-muted'}>{style.label}</span>
          {typeof e.chunkIndex === 'number' && (
            <span className="vault-text-subtle"> · Block {e.chunkIndex + 1}{e.totalChunks ? ` von ${e.totalChunks}` : ''}</span>
          )}
        </span>
        <span className="text-[10px] font-mono vault-text-muted">{fmtMs(e.ms)}</span>
      </div>

      {e.fileName && <div className="text-[10px] vault-text-subtle truncate mt-0.5">{e.fileName}</div>}

      {(e.kind === 'encrypt' || e.kind === 'decrypt') && (
        <div className="text-[11px] font-mono vault-text-subtle mt-1">
          {e.kind === 'encrypt'
            ? <>{fmtBytes(e.plainBytes)} Klartext → {fmtBytes(e.cipherBytes)} Geheimtext{overhead !== null ? ` (+${overhead} Bytes: 12 IV, 16 Auth-Tag)` : ''}</>
            : <>{fmtBytes(e.cipherBytes)} Geheimtext → {fmtBytes(e.plainBytes)} Klartext</>}
        </div>
      )}

      {e.kind === 'sign' && (
        <div className="text-[11px] font-mono vault-text-subtle mt-1">
          an datenputzer.de, mit Anmeldung, 0 Bytes Inhalt → Adresse für {e.targetHost || 'den Speicher'}
        </div>
      )}

      {(e.kind === 'put' || e.kind === 'get') && (
        <div className="text-[11px] font-mono vault-text-subtle mt-1">
          {fmtBytes(e.cipherBytes)} {e.kind === 'put' ? 'an' : 'von'} {e.targetHost}, ohne Anmeldung
          {e.objectPath && <div className="truncate">Objekt: {e.objectPath}</div>}
        </div>
      )}

      {Array.isArray(e.fingerprint) && (
        <div className="vault-encrypt-hex mt-1.5" aria-hidden="true">
          {e.fingerprint.map((h, i) => <span key={i}>{h}</span>)}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- Probelauf */

function ProbeTab({ getEncryptionKey }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showBytes, setShowBytes] = useState(false);
  const inputRef = useRef(null);

  const run = useCallback(async (file) => {
    setBusy(true); setError(null); setResult(null); setShowBytes(false);
    try {
      let key = getEncryptionKey ? getEncryptionKey() : null;
      const usedSessionKey = Boolean(key);
      if (!key) {
        key = await globalThis.crypto.subtle.generateKey(
          { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
        );
      }

      const sliceLen = Math.min(file.size, CHUNK_SIZE);
      const plainBuf = await file.slice(0, sliceLen).arrayBuffer();
      const plainBytes = new Uint8Array(plainBuf.slice(0));

      const fileId = `probelauf-${globalThis.crypto.randomUUID ? globalThis.crypto.randomUUID() : Math.round(performance.now())}`;

      const t0 = performance.now();
      const cipher = await encryptChunk(plainBuf, key, fileId, 0);
      const encMs = performance.now() - t0;

      const t1 = performance.now();
      const back = await decryptChunk(cipher.buffer.slice(0), key, fileId, 0);
      const decMs = performance.now() - t1;

      const backBytes = new Uint8Array(back);
      let identical = backBytes.length === plainBytes.length;
      if (identical) {
        for (let i = 0; i < backBytes.length; i++) {
          if (backBytes[i] !== plainBytes[i]) { identical = false; break; }
        }
      }

      setResult({
        fileName: file.name,
        fileSize: file.size,
        sliceLen,
        plainBytes,
        cipherBytes: cipher,
        cipherLen: cipher.length,
        fingerprint: chunkFingerprint(cipher),
        encMs,
        decMs,
        identical,
        usedSessionKey,
        inWorker: isWorkerActive(),
        mbPerSec: encMs > 0 ? (sliceLen / (1024 * 1024)) / (encMs / 1000) : null,
      });
    } catch (e) {
      setError(e?.message || 'Probelauf fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }, [getEncryptionKey]);

  return (
    <div className="space-y-3">
      <Note>
        <strong className="vault-text">Der Probelauf sendet nichts.</strong> Er verschlüsselt die
        ersten {fmtBytes(CHUNK_SIZE)} einer Datei Ihrer Wahl mit demselben Code, den auch der
        echte Upload benutzt, und entschlüsselt sie sofort wieder. Es entsteht keine Datei im
        Tresor und es geht kein Byte ins Netz. Genau das ist der stärkste Teil: Lassen Sie den
        Netzwerk-Tab währenddessen offen. Er bleibt leer. Eine leere Liste lässt sich viel
        leichter prüfen als die Frage, ob in einem echten Upload irgendwo Klartext steckt.
      </Note>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) run(f); e.target.value = ''; }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="vault-btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
        {busy ? 'Läuft' : 'Datei auswählen und Probelauf starten'}
      </button>

      {error && (
        <div className="vault-banner-warn rounded-lg p-3 text-xs vault-text-warn flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <>
          <div className="vault-glass rounded-lg p-3">
            <Row label="Datei" value={result.fileName} mono={false} />
            <Row label="Gelesen" value={`${fmtBytes(result.sliceLen)} von ${fmtBytes(result.fileSize)}`} />
            <Row label="Klartext hinein" value={`${result.sliceLen.toLocaleString('de-DE')} Bytes`} />
            <Row label="Geheimtext heraus" value={`${result.cipherLen.toLocaleString('de-DE')} Bytes`} />
            <Row label="Unterschied" value={`+${result.cipherLen - result.sliceLen} Bytes (12 Bytes IV vorn, 16 Bytes Auth-Tag hinten)`} />
            <Row label="Verschlüsseln" value={fmtMs(result.encMs)} />
            <Row label="Entschlüsseln" value={fmtMs(result.decMs)} />
            <Row label="Ausgeführt in" value={result.inWorker ? 'Web Worker (eigener Strang)' : 'Hauptstrang (Worker nicht verfügbar)'} mono={false} />
            <Row
              label="Schlüssel"
              value={result.usedSessionKey ? 'Ihr entsperrter Tresorschlüssel' : 'Wegwerfschlüssel, weil der Tresor gesperrt ist'}
              mono={false}
            />
          </div>

          <div className={`rounded-lg p-3 text-xs flex items-start gap-2 ${result.identical ? 'vault-glass' : 'vault-banner-warn'}`}>
            {result.identical
              ? <><Check className="w-4 h-4 shrink-0 mt-0.5 vault-text-accent" /><span className="vault-text">Zurückentschlüsselt und Byte für Byte mit dem Original verglichen. Identisch.</span></>
              : <><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span className="vault-text-warn">Rückweg weicht ab. Das darf nicht passieren, bitte melden Sie das.</span></>}
          </div>

          {result.mbPerSec && (
            <Note>
              <strong className="vault-text">Durchsatz der Verschlüsselung auf diesem Gerät: {result.mbPerSec.toFixed(0)} MB/s.</strong>{' '}
              Gemessen wurde reine Rechenzeit, ohne Übertragung. Die Dauer eines Uploads ergibt sich
              aus der Bandbreite Ihrer Verbindung.
            </Note>
          )}

          <div>
            <button type="button" onClick={() => setShowBytes((v) => !v)} className="vault-btn-ghost text-xs px-2 py-1">
              {showBytes ? 'Bytes verbergen' : 'Erste 32 Bytes zeigen'}
            </button>
            {showBytes && (
              <div className="mt-2 space-y-2">
                <Note tone="warn">
                  Achtung, das hier sind die ersten Bytes Ihrer echten Datei im Klartext. Zeigen Sie
                  diesen Bereich nicht her, wenn jemand mitschaut.
                </Note>
                <div className="vault-glass rounded-lg p-3">
                  <div className="text-[10px] vault-text-subtle mb-1">Klartext, wie er in die Verschlüsselung geht</div>
                  <div className="text-[10px] font-mono vault-text break-all">{hexDump(result.plainBytes)}</div>
                  <div className="text-[10px] font-mono vault-text-muted mt-1 break-all">{asciiDump(result.plainBytes)}</div>
                </div>
                <div className="vault-glass rounded-lg p-3">
                  <div className="text-[10px] vault-text-subtle mb-1">Geheimtext, wie er das Gerät verlassen würde. Die ersten 12 Bytes sind der IV.</div>
                  <div className="text-[10px] font-mono vault-text break-all">{hexDump(result.cipherBytes)}</div>
                  <div className="text-[10px] font-mono vault-text-muted mt-1 break-all">{asciiDump(result.cipherBytes)}</div>
                </div>
                <div className="vault-glass rounded-lg p-3">
                  <div className="text-[10px] vault-text-subtle mb-1">Prüfsumme aus dem Auth-Tag, dieselbe Anzeige wie beim echten Upload</div>
                  <div className="vault-encrypt-hex" aria-hidden="true">
                    {result.fingerprint.map((h, i) => <span key={i}>{h}</span>)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Selbsttests */

const TEST_DEFS = [
  {
    id: 'export',
    title: 'Schlüssel auslesen',
    expect: 'Der Browser muss das verweigern.',
    explain: 'Der Schlüssel ist als nicht auslesbar angelegt. Selbst unser eigener Code kommt an die rohen Bytes nicht heran.',
  },
  {
    id: 'flip',
    title: 'Ein einzelnes Bit kippen',
    expect: 'Das Entschlüsseln muss scheitern.',
    explain: 'Auf einer Kopie des Geheimtextes wird ein Bit verändert. AES-GCM erkennt das über den Auth-Tag. Der echte Upload bleibt unangetastet.',
  },
  {
    id: 'reorder',
    title: 'Block an falscher Stelle',
    expect: 'Das Entschlüsseln muss scheitern.',
    explain: 'Der Geheimtext von Block 1 wird als Block 2 ausgegeben. Die Blocknummer steckt in den Zusatzdaten der Verschlüsselung, deshalb passt es nicht mehr.',
  },
];

function TestsTab({ getEncryptionKey }) {
  const [results, setResults] = useState({});
  const [busyId, setBusyId] = useState(null);

  const sessionKey = getEncryptionKey ? getEncryptionKey() : null;

  const runTest = useCallback(async (id) => {
    setBusyId(id);
    try {
      let key = sessionKey;
      const usedSessionKey = Boolean(key);
      if (!key) {
        key = await globalThis.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      }

      let passed = false;
      let detail = '';

      if (id === 'export') {
        try {
          await globalThis.crypto.subtle.exportKey('raw', key);
          detail = 'Der Browser hat den Schlüssel herausgegeben. Das wäre ein Fehler.';
        } catch (e) {
          passed = true;
          detail = `Verweigert: ${e?.name || 'Fehler'}`;
        }
      } else {
        const sample = globalThis.crypto.getRandomValues(new Uint8Array(4096));
        const fileId = 'selbsttest';
        const cipher = await encryptChunk(sample.buffer.slice(0), key, fileId, 0);

        if (id === 'flip') {
          const copy = new Uint8Array(cipher); // Kopie, das Original bleibt unberührt
          const pos = Math.floor(copy.length / 2);
          copy[pos] ^= 0x01;
          try {
            await decryptChunk(copy.buffer, key, fileId, 0);
            detail = 'Der veränderte Block ließ sich entschlüsseln. Das wäre ein Fehler.';
          } catch (e) {
            passed = true;
            detail = `Abgewiesen an Byte ${pos}: ${e?.name === 'OperationError' ? 'OperationError' : (e?.message || 'Fehler')}`;
          }
        }

        if (id === 'reorder') {
          try {
            await decryptChunk(cipher.buffer.slice(0), key, fileId, 1);
            detail = 'Der Block ließ sich an falscher Stelle entschlüsseln. Das wäre ein Fehler.';
          } catch (e) {
            passed = true;
            detail = `Abgewiesen: ${e?.name === 'OperationError' ? 'OperationError' : (e?.message || 'Fehler')}`;
          }
        }
      }

      setResults((prev) => ({ ...prev, [id]: { passed, detail, usedSessionKey } }));
    } catch (e) {
      setResults((prev) => ({ ...prev, [id]: { passed: false, detail: e?.message || 'Test fehlgeschlagen.' } }));
    } finally {
      setBusyId(null);
    }
  }, [sessionKey]);

  return (
    <div className="space-y-3">
      <Note>
        Drei Proben, die vor Ihren Augen laufen. Sie rechnen auf Kopien und auf Zufallsdaten.
        Kein echter Upload und keine gespeicherte Datei wird dabei angefasst.
        {!sessionKey && (
          <> Weil der Tresor gerade gesperrt ist, laufen die Tests mit einem Wegwerfschlüssel.
          Sie zeigen dann, dass der Mechanismus greift, aber nicht, dass genau Ihr Schlüssel
          nicht auslesbar ist. Für diese Aussage entsperren Sie den Tresor und starten Sie den Test erneut.</>
        )}
      </Note>

      {TEST_DEFS.map((t) => {
        const r = results[t.id];
        return (
          <div key={t.id} className="vault-glass rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium vault-text">{t.title}</div>
                <div className="text-xs vault-text-subtle mt-0.5">Erwartet: {t.expect}</div>
                <div className="text-xs vault-text-muted mt-1 leading-relaxed">{t.explain}</div>
              </div>
              <button
                type="button"
                onClick={() => runTest(t.id)}
                disabled={busyId !== null}
                className="vault-btn-secondary mr-test-btn text-xs px-3 py-1.5 shrink-0"
              >
                {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ausführen'}
              </button>
            </div>
            {r && (
              <div className={`mt-2 pt-2 border-t border-[var(--vault-border)] text-xs flex items-start gap-2 ${r.passed ? 'vault-text' : 'vault-text-warn'}`}>
                {r.passed ? <Check className="w-4 h-4 shrink-0 vault-text-accent" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                <span className="font-mono [overflow-wrap:anywhere]">
                  {r.detail}
                  {r.usedSessionKey === false && ' · Wegwerfschlüssel'}
                  {r.usedSessionKey === true && ' · Ihr Tresorschlüssel'}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------- Quelltext */

function SourceTab() {
  const [hashes, setHashes] = useState({});
  const [busy, setBusy] = useState(false);

  const files = useMemo(() => {
    const list = [];
    list.push({
      name: 'Krypto-Worker',
      url: CRYPTO_WORKER_URL,
      what: 'Verschlüsselt und entschlüsselt jeden Block. Diese Datei ist absichtlich winzig und kennt kein Passwort und keine Netzwerkfunktion.',
    });
    list.push({
      name: 'Download-Helfer',
      url: new URL('/herunterladen/sw.js', window.location.origin).href,
      what: 'Reicht beim Herunterladen großer Dateien die in Ihrem Browser entschlüsselten Blöcke an den Download weiter. Kennt keinen Schlüssel, macht keine Netzwerkanfragen und speichert nichts.',
    });
    for (const s of document.scripts) {
      if (s.src && s.type === 'module') {
        list.push({
          name: 'Anwendungs-Bündel',
          url: s.src,
          what: 'Die gesamte Oberfläche samt Schlüsselableitung. Groß, aber durchsuchbar.',
        });
      }
    }
    return list;
  }, []);

  const hashAll = useCallback(async () => {
    setBusy(true);
    const next = {};
    for (const f of files) {
      try {
        const res = await fetch(f.url, { cache: 'force-cache' });
        const buf = await res.arrayBuffer();
        next[f.url] = { hash: await sha256Hex(buf), bytes: buf.byteLength };
      } catch (e) {
        next[f.url] = { error: e?.message || 'nicht lesbar' };
      }
    }
    setHashes(next);
    setBusy(false);
  }, [files]);

  return (
    <div className="space-y-3">
      <Note>
        Das sind die Adressen, unter denen Ihr Browser den Code gerade geladen hat. Sie sind
        direkt anklickbar. Der Dateiname enthält eine Kennung aus dem Inhalt, das heißt jede
        Änderung am Code ergibt einen anderen Namen.
      </Note>

      {files.map((f) => {
        const h = hashes[f.url];
        return (
          <div key={f.url} className="vault-glass rounded-lg p-3">
            <div className="text-sm font-medium vault-text">{f.name}</div>
            <div className="text-xs vault-text-muted mt-1 leading-relaxed">{f.what}</div>
            <a
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-mono vault-text-accent break-all mt-2 inline-block hover:underline"
            >
              {f.url}
            </a>
            {h && (
              <div className="mt-2 pt-2 border-t border-[var(--vault-border)]">
                {h.error
                  ? <div className="text-xs vault-text-warn">{h.error}</div>
                  : (
                    <>
                      <Row label="Größe" value={`${h.bytes.toLocaleString('de-DE')} Bytes`} />
                      <div className="text-[10px] vault-text-subtle mt-1">SHA-256</div>
                      <div className="text-[10px] font-mono vault-text break-all">{h.hash}</div>
                    </>
                  )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={hashAll}
        disabled={busy}
        className="vault-btn-secondary w-full py-2 text-sm flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCode2 className="w-4 h-4" />}
        {busy ? 'Wird berechnet' : 'Prüfsummen im Browser berechnen'}
      </button>

      <Note>
        <strong className="vault-text">Was diese Prüfsummen wert sind.</strong> Berechnet wird,
        was ein erneuter Abruf der Adresse liefert, nicht der Code, der in diesem Moment in Ihrem
        Browser läuft. Wer das ohne uns nachrechnen will, lädt dieselbe Adresse mit einem eigenen
        Werkzeug und vergleicht. Kommt derselbe Wert heraus, hat der Server allen dasselbe
        ausgeliefert.
      </Note>

      <Note tone="warn">
        <strong className="vault-text">Die Grenze, die wir nicht wegreden.</strong> Der Code kommt
        bei jedem Aufruf frisch von unserem Server. Ein Betreiber, der dazu gezwungen wird, könnte
        einer einzelnen Anmeldung eine veränderte Fassung schicken. Keine Prüfung im Browser
        schließt das aus. Wer diese Sorge hat, prüft den Code von außen und über mehrere Abrufe.
      </Note>
    </div>
  );
}
